import React, {useEffect, useMemo, useState, useRef} from 'react'
import {eventosOrdinariosFields} from "./eventos-ordinarios-fields"
import {Stack} from "@mui/material"
import PropTypes from "prop-types"
import {pasteTableFromClipboard} from "../../../../utils/csv-utils"
import {normalizeDecimalInput} from "../../../../utils/convert-fields"
import {formatDecimalSeparator} from "src/v2/utils/formatter/format-number"
import {copyTableToClipboard} from "src/v2/utils/table-clipboard"
import MotorEventosOrdinariosService from "../../../../../../../services/motor-eventos-ordinarios-service"
import DynamicColumnTable from "../../../componentes/dynamic-column-table"
import {eventosOrdinariosDecimalFields, mapEventosOrdinariosToTable} from "./map-eventos-ordinarios"
import {useLocation} from "react-router-dom"
import {useDraftLoadGuard} from "../../../use-draft-load-guard"
import {dispatchSnackbar} from "../../../../../../components/SnackBar/snackbar-bus"

const EventosOrdinariosTable = (
    {
        setLoading,
        idCaracteristica,
        eventoPublicado,
        tableData,
        jurosOptions,
        setTableData,
        clearTable,
        onDraftEdit,
    }) => {
    const eventosService = useMemo(() => new MotorEventosOrdinariosService(), [])
    const [tableKey, setTableKey] = useState(Math.random() * 10)
    const location = useLocation()
    const navigationState = location.state
    const {beginDraftLoad, onLocalEdit} = useDraftLoadGuard(idCaracteristica, tableData, onDraftEdit)

    useEffect(() => {
        const {origem} = navigationState ?? {}
        if (idCaracteristica != null && (origem === undefined || origem === "ver")) {
            loadEventosOrdinarios(beginDraftLoad())
        }
    }, [beginDraftLoad, idCaracteristica, navigationState])

    const duplicacaoAplicadaRef = useRef(false)

    useEffect(() => {
        if (navigationState?.origem !== "duplicar") return
        if (duplicacaoAplicadaRef.current) return


        const {
            Data,
            CETIP,
            VersaoDocumento,
        } = navigationState

        const eventosOrdinarios =
            Data?.EventosOrdinarios ?? []

        const idEmissao =
            Data?.Caracteristica
                ?.Caracteristica
                ?.IDEmissao

        const formatted =
            mapEventosOrdinariosToTable(
                eventosOrdinarios,
                CETIP,
                VersaoDocumento,
                idEmissao
            )

        setTableData(formatted)
        setTableKey(Math.random() * 10)

        
        duplicacaoAplicadaRef.current = true
    }, [
        navigationState?.origem,
        navigationState?.CETIP,
        navigationState?.VersaoDocumento,
    ])

    const loadEventosOrdinarios = async (guard) => {
        let failed = false
        setLoading(true)
        try {
            const response = await eventosService.getEventosOrdinarios(idCaracteristica)
            if (!guard.canApply()) return

            const {body} = response
            if (!Array.isArray(body)) throw new Error("Resposta de eventos ordinários inválida")

            if (body.length > 0) {
                eventoPublicado(true)
                const formatted = mapEventosOrdinariosToTable(body)
                setTableData(formatted)
                setTableKey(Math.random() * 10)
            } else {
                await loadEventosOrdinariosTemporario(guard)
            }
        } catch {
            if (guard.canApply()) {
                failed = true
                dispatchSnackbar("Não foi possível carregar os eventos ordinários.", "error")
            }
        } finally {
            if (guard.isCurrent()) setLoading(failed ? "error" : false)
        }
    }

    const loadEventosOrdinariosTemporario = async (guard) => {
        try {
            const response = await eventosService.getTemporarioEventosOrdinarios(idCaracteristica)
            if (!guard.canApply()) return

            const bodyTemp = response?.body?.Eventos || response?.data?.Eventos
            if (!Array.isArray(bodyTemp)) throw new Error("Resposta de eventos ordinários inválida")

            if (bodyTemp?.length > 0) {
                eventoPublicado(false)
                const formatted = mapEventosOrdinariosToTable(bodyTemp)
                setTableData(formatted)
                setTableKey(Math.random() * 10)
            }
        } catch (error) {
            if (error?.status !== 400) throw error
        }
    }

    const copyDataEventosOrdinarios = () =>
        copyTableToClipboard(
            tableData,
            eventosOrdinariosFields(jurosOptions).map(field => field.key),
            (field, value) => eventosOrdinariosDecimalFields[field]
                ? formatDecimalSeparator(normalizeDecimalInput(value), "")
                : value
        )

    const normalizePastedRows = (rows) =>
        rows.map((item, index) =>
            Object.fromEntries(
                Object.entries(item).map(([key, value]) => {
                    if (!eventosOrdinariosDecimalFields[key]) return [key, value]
                    try {
                        return [key, normalizeDecimalInput(value)]
                    } catch (_error) {
                        throw new Error(`Linha ${index + 2}: valor inválido em ${eventosOrdinariosDecimalFields[key]}.`)
                    }
                })
            )
        )

    const pasteDataEventosOrdinario = async () => {
        await pasteTableFromClipboard(
            eventosOrdinariosFields(jurosOptions),
            (payload) => {
                const rows = Array.isArray(payload)
                    ? payload
                    : payload?.data ?? []

                const normalizedRows = normalizePastedRows(rows)
                onLocalEdit()
                setTableData(normalizedRows)
            },
            {validateHeader: true}
        )
    }

    return <Stack sx={{mt: 0}}>
        <DynamicColumnTable
            key={tableKey}
            height={600}
            actionButtons={true}
            copyData={() => copyDataEventosOrdinarios()}
            pasteData={pasteDataEventosOrdinario}
            onUserEdit={onLocalEdit}
            fields={eventosOrdinariosFields(jurosOptions)}
            items={tableData}
            setItems={setTableData}
            clearTable={() => {
                onLocalEdit()
                clearTable(setTableData)
            }}
        />
    </Stack>
}

EventosOrdinariosTable.propTypes = {
    setLoading: PropTypes.func,
    idCaracteristica: PropTypes.number,
    eventoPublicado: PropTypes.func,
    tableData: PropTypes.array,
    setTableData: PropTypes.func,
    onDraftEdit: PropTypes.func,
    jurosOptions: PropTypes.array,
    clearTable: PropTypes.func,
}

export default EventosOrdinariosTable
